import React from "react";
import { Alert, Nav, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { AppState } from "../../store/root.reducer";
import User from "../../models/user.model";
import { BatchCreatingArtworks } from "../../components/web3/batch.artwork";
import { SketchfabImportWorkspaceTab } from "../../components/artwork-workspace/SketchfabImportWorkspaceTab";
import { ArtworksWorkspaceTab } from "../../components/artwork-workspace/ArtworksWorkspaceTab";
import { DatasetsWorkspaceTab } from "../../components/artwork-workspace/DatasetsWorkspaceTab";
import { ArtworkWorkspaceConfig, ArtworkWorkspaceTab, ArtworkWorkspaceViewMode } from "../../types/artworkWorkspace";
import { consumeSketchfabOAuthCallback } from "../../components/profile/security.callback";
import { ApiClient } from "../../utils/apiClient";
import { trackWorkspaceEvent } from "../../utils/workspaceTelemetry";
import workspaceTheme from "../../components/artwork-workspace/workspaceTheme.module.css";
import { ArtworkWorkspaceCreateEditor } from "./workspace.detail";
import { canAccessArtworkWorkspace, getNormalizedUserRoles } from "../../utils/userAccess";
import { PendingOptimizationsWorkspaceTab } from "../../components/artwork-workspace/PendingOptimizationsWorkspaceTab";
import { usePendingArtworkOptimizations } from "../../components/artworks/usePendingArtworkOptimizations";

const BASE_API = process.env.REACT_APP_BASE_API || "";
const apiClient = ApiClient(BASE_API);
const TAB_ORDER: ArtworkWorkspaceTab[] = ["artworks", "add-artwork", "datasets", "sketchfab-import"];

const DEFAULT_TAB: ArtworkWorkspaceTab = "artworks";

export const ArtworkWorkspacePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useSelector((state: AppState) => state.auth.user as User | null);
  const canAccessWorkspace = canAccessArtworkWorkspace(user);
  const workspaceAccessKey = [
    user?._id || "",
    user?.role || "",
    getNormalizedUserRoles(user).sort().join("|"),
    user?.curator ? "1" : "0",
    user?.institutionId || "",
  ].join("::");
  const [config, setConfig] = React.useState<ArtworkWorkspaceConfig | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [accessDenied, setAccessDenied] = React.useState(false);
  const [sanitizedMessage, setSanitizedMessage] = React.useState<string | null>(null);
  const visibleTabs = React.useMemo(
    () => TAB_ORDER.filter((tab) => (config?.visibleTabs || []).includes(tab)),
    [config?.visibleTabs]
  );
  const rawTab = (searchParams.get("tab") || DEFAULT_TAB) as ArtworkWorkspaceTab;
  const activeTab = visibleTabs.includes(rawTab) ? rawTab : (visibleTabs[0] || DEFAULT_TAB);
  const rawAddMode = searchParams.get("addMode") || "single";
  const optimizationAvailable = Boolean(config?.optimization?.enabled && config.optimization.allowedForCurrentUser);
  const addMode = rawAddMode === "multi" || (rawAddMode === "pending" && optimizationAvailable)
    ? rawAddMode
    : "single";
  const pendingOptimizations = usePendingArtworkOptimizations(
    optimizationAvailable,
    activeTab === "add-artwork" && addMode === "pending"
  );
  const sketchfabView = (searchParams.get("skView") || searchParams.get("artView") || "cards") as ArtworkWorkspaceViewMode;

  React.useEffect(() => {
    const callback = consumeSketchfabOAuthCallback({
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
    });

    if (callback) {
      setSanitizedMessage(
        callback.status === "success"
          ? "Sketchfab connected successfully."
          : `Sketchfab connection failed${callback.reason ? `: ${callback.reason}` : "."}`
      );
      navigate(callback.nextUrl, { replace: true });
    }
  }, [navigate]);

  React.useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true);
      setError(null);
      setAccessDenied(false);
      const response = await apiClient.getArtworkWorkspaceConfig();
      if (!alive) {
        return;
      }
      if (!response.status) {
        if (response.error?.code === "FORBIDDEN" || response.error?.code === "UNAUTHORIZED") {
          setAccessDenied(true);
          setConfig(null);
          setLoading(false);
          return;
        }
        setError(response.error?.message || "Failed to load workspace.");
        setConfig(null);
        setLoading(false);
        trackWorkspaceEvent("workspace_config_failed", {
          message: response.error?.message || "Failed to load workspace.",
        });
        return;
      }
      setConfig(response.data);
      setLoading(false);
      trackWorkspaceEvent("workspace_config_loaded", {
        visibleTabs: response.data.visibleTabs,
      });
    };

    if (!canAccessWorkspace) {
      setLoading(false);
      return;
    }

    void run();
    return () => {
      alive = false;
    };
  }, [canAccessWorkspace, workspaceAccessKey]);

  React.useEffect(() => {
    if (!config || visibleTabs.length === 0 || visibleTabs.includes(rawTab)) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    next.set("tab", activeTab);
    setSearchParams(next, { replace: true });
    if (rawTab !== activeTab) {
      setSanitizedMessage("That tab is not available for your account.");
      trackWorkspaceEvent("workspace_tab_sanitized", {
        requestedTab: rawTab,
        activeTab,
      });
    }
  }, [activeTab, config, rawTab, searchParams, setSearchParams, visibleTabs]);

  React.useEffect(() => {
    if (!config || rawAddMode !== "pending" || optimizationAvailable) return;
    const next = new URLSearchParams(searchParams);
    next.set("addMode", "single");
    next.delete("optimizationJob");
    setSearchParams(next, { replace: true });
  }, [config, optimizationAvailable, rawAddMode, searchParams, setSearchParams]);
  if (!canAccessWorkspace) {
    return <Navigate to="/" replace />;
  }

  if (accessDenied) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <Spinner animation="border" />
      </div>
    );
  }

  if (error || !config) {
    return (
      <div className="container py-4">
        <Alert variant="danger">{error || "Workspace could not be loaded."}</Alert>
      </div>
    );
  }

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };
  const openOptimizationJob = (jobId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", "add-artwork");
    next.set("addMode", "single");
    next.set("optimizationJob", jobId);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="container py-4">
      <div className={workspaceTheme.workspaceShell}>
        <div className={`d-flex justify-content-between gap-3 mb-4 ${workspaceTheme.hero}`}>
          <div>
            <h2 className="mb-1">Artwork Workspace</h2>
          </div>
        </div>

        {sanitizedMessage ? (
          <Alert variant="warning" className={workspaceTheme.surfaceAlert} onClose={() => setSanitizedMessage(null)} dismissible>
            {sanitizedMessage}
          </Alert>
        ) : null}

        <Nav variant="tabs" className={`mb-4 ${workspaceTheme.topTabs}`}>
          {visibleTabs.map((tab) => (
            <Nav.Item key={tab}>
              <Nav.Link active={activeTab === tab} onClick={() => setParam("tab", tab)}>
                {tab === "artworks" ? "Artworks" : tab === "add-artwork" ? "Add Artwork" : tab === "sketchfab-import" ? "Sketchfab Import" : "Datasets"}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        {activeTab === "artworks" ? <ArtworksWorkspaceTab config={config} /> : null}

        {activeTab === "add-artwork" ? (
          <div className="d-flex flex-column gap-3">
            <Nav variant="pills" className={`mb-2 ${workspaceTheme.subTabs}`}>
              <Nav.Item>
                <Nav.Link active={addMode === "single"} onClick={() => setParam("addMode", "single")}>Add/Edit Artwork</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link active={addMode === "multi"} onClick={() => setParam("addMode", "multi")}>Batch Import</Nav.Link>
              </Nav.Item>
              {optimizationAvailable ? (
                <Nav.Item>
                  <Nav.Link active={addMode === "pending"} onClick={() => setParam("addMode", "pending")}>
                    <span className="d-inline-flex align-items-center gap-2">
                      Pending Optimizations
                      {pendingOptimizations.data?.visibleCount ? (
                        <span className={workspaceTheme.pendingOptimizationBadge}>{pendingOptimizations.data.visibleCount}</span>
                      ) : null}
                    </span>
                  </Nav.Link>
                </Nav.Item>
              ) : null}
            </Nav>
            {addMode === "single" ? (
              <ArtworkWorkspaceCreateEditor />
            ) : addMode === "multi" ? (
              <BatchCreatingArtworks />
            ) : (
              <div className={workspaceTheme.detailSection}>
                <PendingOptimizationsWorkspaceTab
                  data={pendingOptimizations.data}
                  loading={pendingOptimizations.loading}
                  error={pendingOptimizations.error}
                  onRefresh={pendingOptimizations.refresh}
                  onOpenJob={(jobId) => openOptimizationJob(jobId)}
                />
              </div>
            )}
          </div>
        ) : null}

        {activeTab === "sketchfab-import" ? (
          config.capabilities.canUseSketchfabImport ? (
            <SketchfabImportWorkspaceTab
              viewMode={sketchfabView}
              onViewModeChange={(mode) => setParam("skView", mode)}
            />
          ) : (
            <Alert variant="warning" className={workspaceTheme.surfaceAlert}>Sketchfab import is limited to institution curators and admins.</Alert>
          )
        ) : null}

        {activeTab === "datasets" ? (
          config.capabilities.canUseDatasets ? (
            <DatasetsWorkspaceTab config={config} />
          ) : (
            <Alert variant="warning" className={workspaceTheme.surfaceAlert}>Datasets are limited to institution curators and admins.</Alert>
          )
        ) : null}
      </div>
    </div>
  );
};
